import React, { useState, useEffect } from 'react'
import {
  Card,
  Button,
  message,
  Spin,
  Result,
  Modal,
  Table,
  Switch,
  Tag,
  Space,
  Typography,
  Divider
} from 'antd'
import {
  GoogleOutlined,
  CheckOutlined,
  LockOutlined,
  ReloadOutlined,
  SettingOutlined,
  LinkOutlined,
  ExperimentOutlined,
  SyncOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import api from '../utils/api'
import Hint from '../components/common/Hint'

const { Title, Text, Paragraph } = Typography

const PageContainer = styled.div`
  padding: 24px;
  background: #fff;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
  @media (max-width: 576px) {
    padding: 12px;

    /* Result 在小螢幕的內距太誇張，會把按鈕擠出畫面 */
    .ant-result {
      padding: 24px 8px;
    }
    .ant-result-icon > .anticon {
      font-size: 56px;
    }
    .ant-result-title {
      font-size: 18px;
    }
    .ant-result-subtitle {
      font-size: 13px;
      word-break: break-all;
    }

    /* Card body 同步縮小 */
    .ant-card-body {
      padding: 16px 12px;
    }
    .ant-card-head {
      padding: 0 12px;
      min-height: 40px;
    }
    .ant-card-head-title {
      padding: 8px 0;
    }
  }
`

const StyledCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
  }
`

const FeatureLockCard = styled(Card)`
  background: linear-gradient(135deg, #fffbe6 0%, #fff7e6 100%);
  border: none;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;

  .ant-card-body {
    padding: 24px;
  }

  .lock-content {
    text-align: center;
    padding: 32px 24px;
  }

  .lock-icon {
    font-size: 48px;
    color: #ffa940;
    margin-bottom: 16px;
  }

  .lock-title {
    color: #434343;
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 12px;
  }

  .lock-description {
    color: #8c8c8c;
    font-size: 14px;
    margin-bottom: 24px;
    max-width: 80%;
    margin-left: auto;
    margin-right: auto;
  }

  .feature-list {
    text-align: left;
    max-width: 320px;
    margin: 24px auto;

    .feature-item {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      color: #595959;

      .anticon {
        color: #52c41a;
        margin-right: 8px;
      }
    }
  }

  @media (max-width: 576px) {
    .lock-content {
      padding: 24px 16px;
    }

    .lock-title {
      font-size: 18px;
    }

    .lock-description {
      font-size: 13px;
      max-width: 100%;
    }
  }
`

const EmployeeCalendarTable = styled(Table)`
  .ant-table-thead > tr > th {
    background: #fafafa;
    font-weight: 600;
  }
  /* 手機版：表格水平捲動，避免擠爆畫面 */
  @media (max-width: 768px) {
    .ant-table {
      font-size: 13px;
    }
    .ant-table-cell {
      padding: 8px 6px !important;
      white-space: nowrap;
    }
  }
`

/* 已連結時，3 個按鈕在窄螢幕會擠出去，這裡改用 wrap + 全寬 */
const ConnectedActions = styled.div`
  margin-top: 24px;
  text-align: center;

  .ant-space {
    flex-wrap: wrap;
    justify-content: center;
    row-gap: 8px;
  }

  @media (max-width: 576px) {
    .ant-space {
      width: 100%;
      gap: 8px !important;
    }
    .ant-space-item {
      width: 100%;
    }
    .ant-btn {
      width: 100%;
    }
  }
`

/* 卡片 head Space 在窄螢幕也可能撐爆，讓它能換行 */
const CardTitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-weight: 600;
`

/* 主標題在手機縮小，避免單行擠到右側 extra */
const PageTitle = styled(Title)`
  &.ant-typography {
    margin-bottom: 24px;
  }
  @media (max-width: 576px) {
    &.ant-typography {
      font-size: 20px !important;
    }
  }
`

const GoogleCalendarSettingsPage = () => {
  const { user } = useAuth()
  const { hasFeature, isBrandPlan } = useMembership()
  const navigate = useNavigate()
  const [connectionStatus, setConnectionStatus] = useState({
    isConnected: false,
    loading: true,
    email: null
  })
  const [employees, setEmployees] = useState([])
  const [loadingEmployees, setLoadingEmployees] = useState(false)
  const [employeeCalendarStatus, setEmployeeCalendarStatus] = useState({})
  const [showSuccess, setShowSuccess] = useState(false)
  const [businessInfo, setBusinessInfo] = useState({
    plan: 'free',
    endDate: null
  })
  const [testing, setTesting] = useState(false)
  const [calendarInfo, setCalendarInfo] = useState(null)
  // 日曆權限實測結果：授權時未勾選「日曆」細項權限會出現
  // 「已連結但無法同步」的假象，後端會實測並回報
  const [calendarAccessible, setCalendarAccessible] = useState(true)
  // Google 憑證已失效（刷新也救不回），需重新授權
  const [tokenExpired, setTokenExpired] = useState(false)

  // 检查 Google Calendar 连接状态
  const checkConnectionStatus = async () => {
    try {
      const response = await api.get('/api/auth/google-calendar/status')
      const isConnected = response.data.connected || false
      const email = response.data.email || null
      const calendarInfo = response.data.calendarInfo || null
      setCalendarAccessible(response.data.calendarAccessible !== false)

      setConnectionStatus({
        isConnected,
        loading: false,
        email
      })

      // 保存日历信息
      if (isConnected && calendarInfo) {
        setCalendarInfo(calendarInfo)
      } else {
        setCalendarInfo(null)
      }
    } catch (error) {
      // 401 TOKEN_EXPIRED＝後端明確判定 Google 憑證已失效（含刷新失敗），
      // 屬預期狀態：顯示未連結並提示重新授權，不視為系統錯誤
      if (error.response?.data?.code === 'TOKEN_EXPIRED') {
        setTokenExpired(true)
      } else {
        console.error('檢查 Google Calendar 連結狀態時出錯：', error)
      }
      setConnectionStatus({
        isConnected: false,
        loading: false,
        email: null
      })
      setCalendarInfo(null)
    }
  }

  // 获取员工列表
  const fetchEmployees = async () => {
    if (!user?.businessId) return
    try {
      setLoadingEmployees(true)
      const response = await api.get(`/api/employees/${user.businessId}`)
      const employeesData = Array.isArray(response.data) ? response.data : []
      setEmployees(employeesData)

      // 获取每个员工的日历状态
      if (employeesData.length > 0) {
        await fetchEmployeeCalendarStatus(employeesData)
      }
    } catch (error) {
      console.error('獲取員工列表失敗：', error)
      message.error('獲取員工列表失敗')
    } finally {
      setLoadingEmployees(false)
    }
  }

  // 获取员工日历状态
  const fetchEmployeeCalendarStatus = async (employeesList) => {
    try {
      const statusMap = {}
      // 預設：所有員工都先填預設值，避免 API 失敗時 UI 卡住
      employeesList.forEach((emp) => {
        statusMap[emp._id] = {
          calendarId: null,
          isEnabled: false,
          calendarName: null
        }
      })

      const response = await api.get(
        `/api/businesses/${user.businessId}/google-calendar/employees/status`
      )
      if (response.data?.success && Array.isArray(response.data.employees)) {
        response.data.employees.forEach((s) => {
          statusMap[s.employeeId] = {
            calendarId: s.calendarId,
            isEnabled: s.isEnabled,
            calendarName: s.calendarName
          }
        })
      }
      setEmployeeCalendarStatus(statusMap)
    } catch (error) {
      console.error('獲取員工日曆狀態失敗：', error)
    }
  }

  // 初始化
  useEffect(() => {
    const init = async () => {
      try {
        // 获取商家信息
        const businessResponse = await api.get(
          `/api/business_B/${user.businessId}`
        )
        setBusinessInfo({
          plan: businessResponse.data.subscription?.plan || 'free',
          endDate: businessResponse.data.subscription?.endDate
        })

        // 检查日历连接状态
        await checkConnectionStatus()

        // 如果是品牌方案，获取员工列表
        if (isBrandPlan()) {
          await fetchEmployees()
        }
      } catch (error) {
        console.error('初始化時出錯：', error)
        setConnectionStatus((prev) => ({ ...prev, loading: false }))
      }
    }

    if (user?.businessId) {
      init()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.businessId])

  // 处理 URL 回调
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const calendarConnected = urlParams.get('calendar_connected')
    const calendarError = urlParams.get('calendar_error')

    if (calendarConnected === 'true') {
      setShowSuccess(true)
      setTimeout(() => setShowSuccess(false), 5000)
      // 延迟检查连接状态，确保后端已处理回调
      setTimeout(() => {
        checkConnectionStatus()
      }, 1000)
    } else if (calendarError) {
      message.error(
        `Google Calendar 連結失敗: ${decodeURIComponent(calendarError)}`
      )
      setConnectionStatus((prev) => ({ ...prev, loading: false }))
    }

    if (calendarConnected || calendarError) {
      window.history.replaceState({}, '', window.location.pathname)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 连接 Google Calendar
  const handleConnect = async () => {
    try {
      const response = await api.get('/api/auth/google-calendar/auth')
      if (response.data && response.data.authUrl) {
        window.location.href = response.data.authUrl
      } else {
        message.error('連結 Google Calendar 時發生錯誤，請稍後再試')
      }
    } catch (error) {
      if (error.response?.status === 400) {
        message.error(error.response.data.error || '連結失敗：未找到商家資訊')
      } else {
        console.error('獲取 Google 授權 URL 時出錯：', error)
        message.error('連結 Google Calendar 時發生錯誤，請稍後再試')
      }
    }
  }

  // 断开连接
  const handleDisconnect = async () => {
    try {
      await api.post('/api/auth/google-calendar/disconnect')
      setConnectionStatus({
        isConnected: false,
        loading: false,
        email: null
      })
      message.success('已成功解除與 Google Calendar 的連結')
    } catch (error) {
      console.error('解除 Google Calendar 連結時出錯：', error)
      message.error('解除連結時發生錯誤，請稍後再試')
    }
  }

  // 切换员工日历同步
  const handleToggleEmployeeCalendar = async (employeeId, enabled) => {
    try {
      await api.put(
        `/api/businesses/${user.businessId}/google-calendar/employees/${employeeId}/sync`,
        { enabled }
      )
      setEmployeeCalendarStatus((prev) => ({
        ...prev,
        [employeeId]: {
          ...prev[employeeId],
          isEnabled: enabled
        }
      }))
      message.success(
        enabled ? '已啟用該服務人員的日曆同步' : '已停用該服務人員的日曆同步'
      )
    } catch (error) {
      console.error('切換員工日曆同步失敗：', error)
      const errMsg = error?.response?.data?.error || '操作失敗，請稍後再試'
      message.error(errMsg)
    }
  }

  // 为员工创建日历
  const handleCreateEmployeeCalendar = async (employeeId) => {
    try {
      const response = await api.post(
        `/api/businesses/${user.businessId}/google-calendar/employees/${employeeId}/calendar`
      )
      if (response.data?.success && response.data.calendar) {
        const cal = response.data.calendar
        setEmployeeCalendarStatus((prev) => ({
          ...prev,
          [employeeId]: {
            calendarId: cal.calendarId,
            calendarName: cal.calendarName,
            isEnabled: cal.isEnabled
          }
        }))
        message.success(
          response.data.alreadyExists
            ? '該員工已有日曆'
            : '已成功建立員工日曆'
        )
      }
    } catch (error) {
      console.error('創建員工日曆失敗：', error)
      const code = error?.response?.data?.code
      const errMsg = error?.response?.data?.error || '創建日曆失敗，請稍後再試'
      if (code === 'INSUFFICIENT_SCOPE') {
        // 引導使用者重新授權
        Modal.warning({
          title: '需要重新授權',
          content:
            '目前 Google 授權範圍不足以建立子日曆。請先點選「解除連結」、再重新「開始連結」一次，授權頁會出現「建立並管理 BeauteFeel 日曆」的勾選項，請務必勾選。',
          okText: '我知道了'
        })
      } else if (code === 'TOKEN_EXPIRED' || code === 'NOT_CONNECTED') {
        message.warning(errMsg)
      } else {
        message.error(errMsg)
      }
    }
  }

  // 创建测试事件
  const handleCreateTestEvent = async () => {
    if (!connectionStatus.isConnected) {
      message.warning('請先連結 Google Calendar')
      return
    }

    setTesting(true)
    try {
      const now = new Date()
      const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000)

      const response = await api.post('/api/auth/google-calendar/test-event', {
        title: '測試事件 - Google Calendar 同步',
        description:
          '這是一個測試事件，用於驗證 Google Calendar 連接是否正常。',
        startTime: now.toISOString(),
        endTime: oneHourLater.toISOString()
      })

      if (response.data.success && response.data.data) {
        const eventData = response.data.data
        const eventUrl = eventData.eventUrl || eventData.htmlLink

        message.success('測試事件已創建！請檢查您的 Google Calendar。')

        if (eventUrl) {
          Modal.info({
            title: '測試事件已創建',
            content: (
              <div>
                <p>測試事件已成功創建到您的 Google Calendar。</p>
                <p style={{ marginTop: 12 }}>
                  <a
                    href={eventUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{ color: '#1890ff' }}
                  >
                    點擊這裡查看事件
                  </a>
                </p>
                {eventData.summary && (
                  <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                    事件標題：{eventData.summary}
                  </p>
                )}
              </div>
            ),
            okText: '知道了'
          })
        }
      } else {
        message.error('創建測試事件失敗：未收到有效響應')
      }
    } catch (error) {
      console.error('創建測試事件失敗：', error)
      if (error.response?.status === 400) {
        const errorMessage = error.response.data?.error || '創建測試事件失敗'
        if (error.response.data?.code === 'NOT_CONNECTED') {
          message.error('未連接 Google Calendar，請先完成授權')
        } else if (error.response.data?.code === 'TOKEN_EXPIRED') {
          message.error('Token 已過期，請重新授權')
        } else {
          message.error(errorMessage)
        }
      } else if (error.response?.status === 404) {
        message.warning('此功能需要後端支持，請聯繫開發人員')
      } else {
        message.error('創建測試事件失敗，請稍後再試')
      }
    } finally {
      setTesting(false)
    }
  }

  // 打开 Google Calendar
  const handleOpenCalendar = () => {
    // 优先使用后端返回的 calendarUrl，如果没有则使用默认 URL
    const calendarUrl =
      calendarInfo?.calendarUrl || 'https://calendar.google.com/calendar/r'
    window.open(calendarUrl, '_blank')
  }

  const isFreeUser = businessInfo.plan === 'free'

  if (connectionStatus.loading) {
    return (
      <PageContainer>
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <Spin size='large' tip='載入中...' />
        </div>
      </PageContainer>
    )
  }

  // 免费用户显示锁定页面
  if (isFreeUser) {
    return (
      <PageContainer>
        <FeatureLockCard>
          <div className='lock-content'>
            <LockOutlined className='lock-icon' />
            <div className='lock-title'>升級至商務版，解鎖日曆同步功能</div>
            <div className='lock-description'>
              自動同步預約至 Google
              日曆，輕鬆管理所有預約行程，不錯過任何重要約會
            </div>
            <div className='feature-list'>
              <div className='feature-item'>
                <CheckOutlined /> 預約自動同步至 Google 日曆
              </div>
              <div className='feature-item'>
                <CheckOutlined /> 每位服務人員獨立日曆
              </div>
              <div className='feature-item'>
                <CheckOutlined /> 即時更新預約狀態（成立/取消/改期）
              </div>
              <div className='feature-item'>
                <CheckOutlined /> 多裝置同步行事曆
              </div>
            </div>
          </div>
        </FeatureLockCard>
      </PageContainer>
    )
  }

  // 表格列定义
  const columns = [
    {
      title: '服務人員',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Text strong>{text}</Text>
          {record.phone && <Text type='secondary'>({record.phone})</Text>}
        </Space>
      )
    },
    {
      title: '日曆狀態',
      key: 'calendarStatus',
      render: (_, record) => {
        const status = employeeCalendarStatus[record._id]
        if (!status) {
          return <Tag color='default'>未配置</Tag>
        }
        if (status.calendarId) {
          return (
            <Tag color='success'>{status.calendarName || '已創建日曆'}</Tag>
          )
        }
        return <Tag color='warning'>未創建日曆</Tag>
      }
    },
    {
      title: '同步開關',
      key: 'syncEnabled',
      render: (_, record) => {
        const status = employeeCalendarStatus[record._id]
        return (
          <Switch
            checked={status?.isEnabled || false}
            onChange={(checked) =>
              handleToggleEmployeeCalendar(record._id, checked)
            }
            disabled={!status?.calendarId || !connectionStatus.isConnected}
          />
        )
      }
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        const status = employeeCalendarStatus[record._id]
        return (
          <Space>
            {!status?.calendarId && (
              <Button
                type='link'
                size='small'
                onClick={() => handleCreateEmployeeCalendar(record._id)}
                disabled={!connectionStatus.isConnected}
              >
                創建日曆
              </Button>
            )}
            {status?.calendarId && (
              <Button
                type='link'
                size='small'
                icon={<ReloadOutlined />}
                onClick={() => handleCreateEmployeeCalendar(record._id)}
              >
                重新同步
              </Button>
            )}
          </Space>
        )
      }
    }
  ]

  return (
    <PageContainer>
      {showSuccess && (
        <Hint
          title='Google Calendar 連結成功！'
          tone='success'
          closable
          onClose={() => setShowSuccess(false)}
          style={{ marginBottom: '24px' }}
        >
          您的預約現在會自動同步到 Google 日曆。
        </Hint>
      )}

      <PageTitle level={2}>
        <GoogleOutlined /> Google 日曆同步設定
      </PageTitle>

      {/* Google Calendar 连接状态 */}
      <StyledCard
        title={
          <CardTitleWrap>
            <SettingOutlined />
            <span>Google Calendar 連結狀態</span>
          </CardTitleWrap>
        }
      >
        {connectionStatus.isConnected ? (
          <div>
            {!calendarAccessible && (
              <Hint
                tone='error'
                style={{ marginBottom: 16 }}
                title='已連結帳號，但沒有日曆存取權限，預約無法同步'
              >
                原因通常是授權時未勾選「查看及編輯 Google 日曆」的權限。請先點下方「解除連結」，重新連結時務必勾選日曆權限，完成後系統會自動回填近 3 個月與未來的預約。
              </Hint>
            )}
            <Result
              status={calendarAccessible ? 'success' : 'warning'}
              title='Google Calendar 已連結'
              subTitle={
                <div>
                  {connectionStatus.email && (
                    <p style={{ marginBottom: 8 }}>
                      連結帳號：{connectionStatus.email}
                    </p>
                  )}
                  {calendarInfo?.timeZone && (
                    <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
                      時區：{calendarInfo.timeZone}
                    </p>
                  )}
                </div>
              }
            />
            <ConnectedActions>
              <Space size='middle'>
                <Button
                  type='primary'
                  icon={<LinkOutlined />}
                  onClick={handleOpenCalendar}
                >
                  打開 Google Calendar
                </Button>
                <Button
                  icon={<ExperimentOutlined />}
                  onClick={handleCreateTestEvent}
                  loading={testing}
                >
                  創建測試事件
                </Button>
                <Button
                  danger
                  onClick={() => {
                    Modal.confirm({
                      title: '確定要解除連結嗎？',
                      content: '解除連結後，新的預約將不會同步到 Google 日曆。',
                      okText: '確定解除',
                      cancelText: '取消',
                      onOk: handleDisconnect
                    })
                  }}
                >
                  解除連結
                </Button>
              </Space>
            </ConnectedActions>
            <Hint
              title='如何確認連接成功？'
              tone='info'
              style={{ marginTop: 24 }}
            >
              {
                <div>
                  <p style={{ marginBottom: 8 }}>
                    1. 點擊「創建測試事件」按鈕，系統會在您的 Google Calendar
                    中創建一個測試事件
                  </p>
                  <p style={{ marginBottom: 8 }}>
                    2. 點擊「打開 Google Calendar」查看是否有測試事件
                  </p>
                  <p style={{ marginBottom: 0 }}>
                    3. 當您創建新的預約並確認後，預約會自動同步到 Google
                    Calendar
                  </p>
                </div>
              }
            </Hint>
          </div>
        ) : (
          <div>
            {tokenExpired && (
              <Hint
                tone='warning'
                style={{ marginBottom: 16 }}
                title='Google 授權已失效，需要重新連結'
              >
                原有的授權憑證已無法使用（常見原因：曾在 Google 帳戶的第三方應用程式存取權中移除本服務）。請點下方「開始連結」重新授權，過程中請勾選「查看及編輯 Google 日曆」權限，完成後系統會自動回填近 3 個月與未來的預約。
              </Hint>
            )}
            <Result
              status='info'
              title={
                tokenExpired ? '重新連結 Google Calendar' : '連結 Google Calendar'
              }
              subTitle='連結您的 Google 日曆，讓預約自動同步'
              extra={[
                <Button
                  key='connect'
                  type='primary'
                  icon={<GoogleOutlined />}
                  onClick={handleConnect}
                >
                  開始連結
                </Button>
              ]}
            />
          </div>
        )}
      </StyledCard>

      {/* 同步说明 */}
      <StyledCard title='同步說明'>
        <Hint
          title={
            connectionStatus.isConnected ? '預約同步已啟用' : '預約同步未啟用'
          }
          tone={connectionStatus.isConnected ? 'success' : 'info'}
          style={{ marginBottom: '16px' }}
        >
          {
            connectionStatus.isConnected
              ? '所有新的預約都會自動同步到您的 Google 日曆。每位服務人員將擁有獨立的日曆，方便管理。'
              : '請先連結 Google Calendar 以啟用同步功能。'
          }
        </Hint>
        <Paragraph>
          <Text strong>同步規則：</Text>
        </Paragraph>
        <ul>
          <li>
            <Text>
              單向同步：後台 → Google Calendar（僅從系統同步到 Google）
            </Text>
          </li>
          <li>
            <Text>
              預約成立：當預約狀態變為「已確認」時，自動創建 Google Calendar
              事件
            </Text>
          </li>
          <li>
            <Text>
              預約取消：當預約被取消時，自動刪除對應的 Google Calendar 事件
            </Text>
          </li>
          <li>
            <Text>
              預約改期：當預約時間或服務人員變更時，自動更新 Google Calendar
              事件
            </Text>
          </li>
          <li>
            <Text>
              每位服務人員一個日曆：系統會為每位服務人員創建獨立的 Google
              Calendar
            </Text>
          </li>
        </ul>
      </StyledCard>

      {/* 员工日历配置（仅品牌方案） */}
      {isBrandPlan() && connectionStatus.isConnected && (
        <>
          <Divider />
          <StyledCard
            title={
              <CardTitleWrap>
                <SettingOutlined />
                <span>服務人員日曆配置</span>
              </CardTitleWrap>
            }
            extra={
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchEmployees}
                loading={loadingEmployees}
                size='small'
              >
                刷新
              </Button>
            }
          >
            <EmployeeCalendarTable
              columns={columns}
              dataSource={employees}
              rowKey='_id'
              loading={loadingEmployees}
              scroll={{ x: 'max-content' }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 位服務人員`,
                size: 'small'
              }}
              locale={{
                emptyText: '暫無服務人員'
              }}
            />
          </StyledCard>
        </>
      )}
    </PageContainer>
  )
}

export default GoogleCalendarSettingsPage
